import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { collection, getDocs, onSnapshot, query, where, limit } from 'firebase/firestore';
import { db } from '../utils/firebase';
import { collectionPath } from '../utils/firestoreCollections';
import { useAuth } from '../components/auth/AuthContext';
import { usePermissions } from '../hooks/usePermissions';
import { useDebounce } from '../hooks/useDebounce';
import { useAddon } from '../hooks/useAddon';
import { useRecommendationActions } from '../hooks/useRecommendationActions';
import { ADDONS } from '../utils/addons';
import { FEATURE_PERMISSIONS, canModifyData, canViewAllData, getRbacMatchNames } from '../utils/permissions';
import {
  OPEN_STATUSES, REC_TYPE_META, PRIORITY_META, HEALTH_BAND_META, sortRecommendations, effectiveStatus
} from '../utils/recommendations';
import RecommendationCard from '../components/recommendations/RecommendationCard';
import PaginationControls from '../components/common/PaginationControls';
import Toast from '../components/common/Toast';
import Tooltip from '../components/common/Tooltip';

const PAGE_SIZE = 25;
// Long per-customer lists are capped until the user asks for more.
const GROUP_PREVIEW = 5;

const RecommendationsPage = ({ user: userProp }) => {
  const { currentUser } = useAuth();
  const user = userProp || currentUser;
  const { can } = usePermissions(user);
  const { enabled: addonEnabled, loaded: addonLoaded } = useAddon(ADDONS.AI_SALES_AGENT);

  const [recs, setRecs] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState(null);
  const [search, setSearch] = useState('');
  const [repFilter, setRepFilter] = useState('all');
  const [priorityFilter, setPriorityFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [bandFilter, setBandFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('proposed');
  const [currentPage, setCurrentPage] = useState(1);
  const [viewMode, setViewMode] = useState('grouped');
  const [expandedGroups, setExpandedGroups] = useState(() => new Set());
  const [fullGroups, setFullGroups] = useState(() => new Set());
  const debouncedSearch = useDebounce(search, 350);

  const teamView = canViewAllData(user) || can(FEATURE_PERMISSIONS.AI_RECS_VIEW_TEAM);
  const canAct = can(FEATURE_PERMISSIONS.AI_RECS_ACT) && canModifyData(user);
  const { busyId, handlers, modals } = useRecommendationActions({
    user,
    users,
    canAssignAny: can(FEATURE_PERMISSIONS.TASK_ASSIGN_ANY),
    onToast: setToast
  });

  useEffect(() => {
    if (!addonEnabled) { setLoading(false); return undefined; }
    const recsCollection = collection(db, collectionPath('recommendations'));
    let q;
    if (teamView) {
      q = query(recsCollection, where('status', 'in', OPEN_STATUSES));
    } else {
      const repNames = [...new Set([
        user?.displayName,
        user?.full_name,
        user?.firstName && user?.lastName ? `${user.firstName} ${user.lastName}` : null,
      ].filter(name => typeof name === 'string' && name.trim()).map(name => name.trim()))];
      if (repNames.length === 0) {
        setRecs([]);
        setLoading(false);
        return undefined;
      }
      q = query(
        recsCollection,
        where('salesRepNames', 'array-contains-any', repNames)
      );
    }
    const unsubscribe = onSnapshot(
      q,
      snap => { setRecs(snap.docs.map(d => ({ id: d.id, ...d.data() }))); setLoading(false); },
      err => { console.error('Failed to load recommendations:', err); setToast({ message: `Error: ${err.message}`, type: 'error' }); setLoading(false); }
    );
    return () => unsubscribe();
  }, [addonEnabled, teamView, user]);

  useEffect(() => {
    if (!addonEnabled) return;
    getDocs(query(collection(db, collectionPath('users')), limit(500)))
      .then(snap => setUsers(snap.docs.map(d => ({ uid: d.id, ...d.data() })).filter(u => u.role !== 'disabled')))
      .catch(err => console.error('Failed to load users:', err));
  }, [addonEnabled]);

  // RBAC: client-side filtering by rep name is intentional (see copilot-instructions)
  const visibleRecs = useMemo(() => {
    if (teamView) return recs;
    const myNames = getRbacMatchNames(user);
    return recs.filter(r => {
      const keys = r.salesRepKeys || (r.salesRepNames || []).map(n => String(n).toLowerCase().trim());
      return myNames.some(n => keys.includes(n));
    });
  }, [recs, teamView, user]);

  const repOptions = useMemo(
    () => [...new Set(visibleRecs.flatMap(r => r.salesRepNames || []))].sort(),
    [visibleRecs]
  );

  const filtered = useMemo(() => {
    const s = debouncedSearch.trim().toLowerCase();
    return visibleRecs
      .filter(r => statusFilter === 'all' || effectiveStatus(r) === statusFilter)
      .filter(r => repFilter === 'all' || (r.salesRepNames || []).includes(repFilter))
      .filter(r => priorityFilter === 'all' || r.priority === priorityFilter)
      .filter(r => typeFilter === 'all' || r.type === typeFilter)
      .filter(r => bandFilter === 'all' || r.healthBand === bandFilter)
      .filter(r => !s || (r.customerName || '').toLowerCase().includes(s) || (r.title || '').toLowerCase().includes(s))
      .sort(sortRecommendations);
  }, [visibleRecs, statusFilter, repFilter, priorityFilter, typeFilter, bandFilter, debouncedSearch]);

  useEffect(() => { setCurrentPage(1); }, [statusFilter, repFilter, priorityFilter, typeFilter, bandFilter, debouncedSearch, viewMode]);

  // `filtered` is already priority-sorted, so each group's recs stay in priority order.
  const groups = useMemo(() => {
    const byCustomer = new Map();
    for (const r of filtered) {
      const key = r.customerId || r.customerName || r.id;
      if (!byCustomer.has(key)) {
        byCustomer.set(key, { key, customerId: r.customerId, customerName: r.customerName, healthBand: r.healthBand, reps: new Set(), recs: [], counts: { high: 0, medium: 0, low: 0 } });
      }
      const g = byCustomer.get(key);
      g.recs.push(r);
      (r.salesRepNames || []).forEach(n => g.reps.add(n));
      if (g.counts[r.priority] !== undefined) g.counts[r.priority] += 1;
      if (!g.healthBand && r.healthBand) g.healthBand = r.healthBand;
    }
    return [...byCustomer.values()].sort((a, b) =>
      b.counts.high - a.counts.high || b.counts.medium - a.counts.medium ||
      b.recs.length - a.recs.length ||
      String(a.customerName || '').localeCompare(String(b.customerName || '')));
  }, [filtered]);

  const pageItems = viewMode === 'grouped' ? groups : filtered;
  const paginated = useMemo(() => pageItems.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE), [pageItems, currentPage]);
  const totalPages = Math.max(1, Math.ceil(pageItems.length / PAGE_SIZE));

  const toggleSet = (setter, key) => setter(prev => {
    const next = new Set(prev);
    if (next.has(key)) next.delete(key); else next.add(key);
    return next;
  });
  const allPageExpanded = viewMode === 'grouped' && paginated.length > 0 && paginated.every(g => expandedGroups.has(g.key));
  const togglePageExpanded = () => setExpandedGroups(prev => {
    const next = new Set(prev);
    paginated.forEach(g => (allPageExpanded ? next.delete(g.key) : next.add(g.key)));
    return next;
  });

  const summary = useMemo(() => {
    const open = visibleRecs.filter(r => effectiveStatus(r) === 'proposed');
    return {
      open: open.length,
      high: open.filter(r => r.priority === 'high').length,
      customers: new Set(open.map(r => r.customerId)).size,
      atRisk: new Set(open.filter(r => r.healthBand === 'At Risk' || r.healthBand === 'Dormant').map(r => r.customerId)).size
    };
  }, [visibleRecs]);

  const filtersActive = search || repFilter !== 'all' || priorityFilter !== 'all' || typeFilter !== 'all' || bandFilter !== 'all' || statusFilter !== 'proposed';

  if (addonLoaded && !addonEnabled) {
    return (
      <main className="flex-grow p-6 overflow-y-auto">
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 text-yellow-800">
          The AI Sales Agent add-on is not enabled for this account. An administrator can enable it in System Settings → Add-ons.
        </div>
      </main>
    );
  }

  return (
    <main className="flex-grow p-4 sm:p-6 overflow-y-auto min-w-0">
      {toast && <Toast {...toast} onClose={() => setToast(null)} />}
      {modals}

      <div className="mb-4">
        <h2 className="text-2xl font-bold text-gray-900">Recommendations</h2>
        <p className="text-sm text-gray-600">
          {teamView ? 'Open recommendations across all reps.' : 'Open recommendations for your customers.'} Suggestions only — you decide what to act on.
        </p>
      </div>

      <div className="flex flex-wrap items-center gap-2 mb-3">
        {[
          ['Open', summary.open, 'Recommendations awaiting a decision'],
          ['High priority', summary.high, 'Open high-priority recommendations'],
          ['Customers', summary.customers, 'Customers with at least one open recommendation'],
          ['At Risk / Dormant', summary.atRisk, 'Customers in At Risk or Dormant health bands with open recommendations']
        ].map(([label, value, tip]) => (
          <Tooltip key={label} text={tip}>
            <div className="bg-white rounded-lg border border-gray-200 px-3 py-1.5 flex items-baseline gap-2">
              <span className="text-lg font-bold text-gray-900">{value}</span>
              <span className="text-xs text-gray-500">{label}</span>
            </div>
          </Tooltip>
        ))}
      </div>

      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-3 mb-3">
        <div className="flex flex-wrap gap-2">
          <input
            type="text"
            placeholder="Search customer or recommendation…"
            value={search}
            onChange={e => setSearch(e.target.value)}
            className={`flex-1 min-w-[200px] border rounded-lg px-3 py-1.5 text-sm transition-colors duration-150 ${search ? 'border-yellow-400 bg-yellow-50' : 'border-gray-300'}`}
          />
          <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} className="border border-gray-300 rounded-lg px-2 py-1.5 text-sm">
            <option value="proposed">Open</option>
            <option value="snoozed">Snoozed</option>
            <option value="all">Open + Snoozed</option>
          </select>
          {teamView && (
            <select value={repFilter} onChange={e => setRepFilter(e.target.value)} className="border border-gray-300 rounded-lg px-2 py-1.5 text-sm">
              <option value="all">All reps</option>
              {repOptions.map(r => <option key={r} value={r}>{r}</option>)}
            </select>
          )}
          <select value={priorityFilter} onChange={e => setPriorityFilter(e.target.value)} className="border border-gray-300 rounded-lg px-2 py-1.5 text-sm">
            <option value="all">All priorities</option>
            {Object.entries(PRIORITY_META).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
          </select>
          <select value={typeFilter} onChange={e => setTypeFilter(e.target.value)} className="border border-gray-300 rounded-lg px-2 py-1.5 text-sm">
            <option value="all">All types</option>
            {Object.entries(REC_TYPE_META).map(([k, v]) => <option key={k} value={k}>{v.icon} {v.label}</option>)}
          </select>
          <select value={bandFilter} onChange={e => setBandFilter(e.target.value)} className="border border-gray-300 rounded-lg px-2 py-1.5 text-sm">
            <option value="all">All health bands</option>
            {Object.keys(HEALTH_BAND_META).map(b => <option key={b} value={b}>{b}</option>)}
          </select>
          {filtersActive && (
            <Tooltip text="Clear filters and reset view">
              <button
                type="button"
                onClick={() => { setSearch(''); setRepFilter('all'); setPriorityFilter('all'); setTypeFilter('all'); setBandFilter('all'); setStatusFilter('proposed'); }}
                className="px-3 py-1.5 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg text-sm font-medium"
              >
                Reset Filters
              </button>
            </Tooltip>
          )}
        </div>
      </div>

      {loading ? (
        <div className="text-gray-500 p-6">Loading recommendations…</div>
      ) : filtered.length === 0 ? (
        <div className="bg-white border border-dashed border-gray-300 rounded-lg p-6 text-center text-gray-600">
          {visibleRecs.length === 0 ? 'No open recommendations right now.' : 'No recommendations match these filters.'}
        </div>
      ) : (
        <>
          <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
            <div className="text-sm text-gray-600">
              {filtered.length} recommendation{filtered.length === 1 ? '' : 's'} across {groups.length} customer{groups.length === 1 ? '' : 's'}
            </div>
            <div className="flex items-center gap-2">
              {viewMode === 'grouped' && (
                <Tooltip text={allPageExpanded ? 'Collapse every customer on this page' : 'Expand every customer on this page'}>
                  <button type="button" onClick={togglePageExpanded} className="px-2 py-1 text-xs rounded border border-gray-300 text-gray-700 hover:bg-gray-50">
                    {allPageExpanded ? 'Collapse all' : 'Expand all'}
                  </button>
                </Tooltip>
              )}
              <div className="inline-flex rounded border border-gray-300 overflow-hidden" role="group" aria-label="View mode">
                {[['grouped', 'By customer', 'One row per customer; expand to see its recommendations'], ['list', 'List', 'Every recommendation as a compact row']].map(([mode, label, tip]) => (
                  <Tooltip key={mode} text={tip}>
                    <button
                      type="button"
                      aria-pressed={viewMode === mode}
                      onClick={() => setViewMode(mode)}
                      className={`px-2 py-1 text-xs ${viewMode === mode ? 'bg-indigo-600 text-white' : 'bg-white text-gray-700 hover:bg-gray-50'}`}
                    >
                      {label}
                    </button>
                  </Tooltip>
                ))}
              </div>
            </div>
          </div>

          {viewMode === 'grouped' ? (
            <div className="bg-white rounded-lg border border-gray-200 divide-y divide-gray-200">
              {paginated.map(g => {
                const isExpanded = expandedGroups.has(g.key);
                const showAll = fullGroups.has(g.key);
                const shown = showAll ? g.recs : g.recs.slice(0, GROUP_PREVIEW);
                const reps = [...g.reps].sort();
                return (
                  <section key={g.key} className="min-w-0">
                    <div className="flex flex-col sm:flex-row sm:items-center gap-2 px-3 py-2 hover:bg-gray-50">
                      <button
                        type="button"
                        onClick={() => toggleSet(setExpandedGroups, g.key)}
                        aria-expanded={isExpanded}
                        title={isExpanded ? 'Collapse' : 'Show recommendations'}
                        className="flex items-center gap-2 min-w-0 flex-1 text-left"
                      >
                        <span className="text-gray-400 text-xs w-3 flex-shrink-0" aria-hidden="true">{isExpanded ? '▾' : '▸'}</span>
                        <span className="font-medium text-gray-900 truncate">{g.customerName || g.customerId}</span>
                        {g.healthBand && (
                          <span className={`text-xs px-2 py-0.5 rounded-full flex-shrink-0 ${HEALTH_BAND_META[g.healthBand] || 'bg-gray-100 text-gray-700'}`}>{g.healthBand}</span>
                        )}
                        {teamView && reps.length > 0 && (
                          <span className="text-xs text-gray-500 truncate hidden md:inline">{reps.join(', ')}</span>
                        )}
                      </button>
                      <div className="flex flex-wrap items-center gap-1.5 pl-5 sm:pl-0 sm:flex-shrink-0">
                        {['high', 'medium', 'low'].filter(p => g.counts[p] > 0).map(p => (
                          <Tooltip key={p} text={`${g.counts[p]} ${PRIORITY_META[p].label.toLowerCase()}-priority`}>
                            <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${PRIORITY_META[p].className}`}>{g.counts[p]} {PRIORITY_META[p].label}</span>
                          </Tooltip>
                        ))}
                        <span className="text-xs text-gray-500">{g.recs.length} total</span>
                        {g.customerId && (
                          <Tooltip text="Open this customer's Recommendations tab">
                            <Link to={`/customer/${encodeURIComponent(g.customerId)}?tab=recommendations`} className="text-xs text-indigo-600 hover:underline ml-1">
                              Open
                            </Link>
                          </Tooltip>
                        )}
                      </div>
                    </div>
                    {isExpanded && (
                      <div className="bg-gray-50 border-t border-gray-100 pl-4">
                        <div className="divide-y divide-gray-100 border-l-2 border-indigo-100">
                          {shown.map(rec => (
                            <RecommendationCard key={rec.id} rec={rec} canAct={canAct} busy={busyId === rec.id} compact {...handlers} />
                          ))}
                        </div>
                        {g.recs.length > GROUP_PREVIEW && (
                          <button type="button" onClick={() => toggleSet(setFullGroups, g.key)} className="text-xs text-indigo-600 hover:underline px-3 py-2">
                            {showAll ? 'Show fewer' : `Show ${g.recs.length - GROUP_PREVIEW} more`}
                          </button>
                        )}
                      </div>
                    )}
                  </section>
                );
              })}
            </div>
          ) : (
            <div className="bg-white rounded-lg border border-gray-200 divide-y divide-gray-200">
              {paginated.map(rec => (
                <div key={rec.id} className="min-w-0">
                  <Link to={`/customer/${encodeURIComponent(rec.customerId)}?tab=recommendations`} className="block px-3 pt-2 text-xs font-medium text-indigo-600 hover:underline truncate">
                    {rec.customerName || rec.customerId}
                  </Link>
                  <RecommendationCard rec={rec} canAct={canAct} busy={busyId === rec.id} compact {...handlers} />
                </div>
              ))}
            </div>
          )}
          <div className="mt-4">
            <PaginationControls currentPage={currentPage} totalPages={totalPages} onPageChange={setCurrentPage} totalItems={pageItems.length} pageSize={PAGE_SIZE} itemLabel={viewMode === 'grouped' ? 'customers' : 'recommendations'} />
          </div>
        </>
      )}
    </main>
  );
};

export default RecommendationsPage;
